import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import {
  UserCog,
  Users,
  ShieldCheck,
  MapPin,
  Building
} from 'lucide-react';
import UserProfile from '../components/UserProfile';
import UserAdministration from '../components/UserAdministration';
import RoleManagement from '../components/RoleManagement';
import LocationsManagement from '../components/LocationsManagement';
import ClientAdministration from '../components/ClientAdministration';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [userRole, setUserRole] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Configuración');
  const navigate = useNavigate();

  useEffect(() => {
    // Obtener el rol del usuario del localStorage
    const role = localStorage.getItem('role');
    setUserRole(role || '');

    // Recuperar la última pestaña activa del localStorage si existe
    const lastTab = localStorage.getItem('settingsLastTab');
    if (lastTab) {
      setActiveTab(lastTab);
    }
  }, []);

  // Guardar la pestaña activa en localStorage cuando cambia
  const handleTabChange = (value) => {
    setActiveTab(value);
    localStorage.setItem('settingsLastTab', value);
  };

  // Determinar qué pestañas debería ver el usuario según su rol
  const isAdmin = userRole === 'admin';

  // Manejar logout
  const handleLogout = () => {
    localStorage.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />

      <div className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        <Topbar showSearch={false} />
        
        <div className="container mx-auto px-4 py-8">
          <Helmet>
            <title>Configuración | OMS AMERISA</title>
          </Helmet>
          
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold">Configuración del Sistema</CardTitle>
                <CardDescription>
                  Administra tu perfil, usuarios y roles del sistema
                </CardDescription>
              </div>
            </CardHeader>
          </Card>
          
          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <UserCog className="h-4 w-4" /> Mi Perfil
              </TabsTrigger>
              
              {isAdmin && (
                <>
                  <TabsTrigger value="users" className="flex items-center gap-2">
                    <Users className="h-4 w-4" /> Administración de Usuarios
                  </TabsTrigger>
                  
                  <TabsTrigger value="clients" className="flex items-center gap-2">
                    <Building className="h-4 w-4" /> Administración de Clientes
                  </TabsTrigger>
                  
                  <TabsTrigger value="roles" className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" /> Roles y Permisos
                  </TabsTrigger>
                  
                  <TabsTrigger value="locations" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Ubicaciones
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <div className="bg-white rounded-lg shadow-md p-6">
              <TabsContent value="profile">
                <h2 className="text-xl font-semibold mb-6">Mi Perfil de Usuario</h2>
                <UserProfile />
              </TabsContent>
              
              {isAdmin && (
                <>
                  <TabsContent value="users">
                    <h2 className="text-xl font-semibold mb-6">Administración de Usuarios</h2>
                    <UserAdministration />
                  </TabsContent>
                  
                  <TabsContent value="clients">
                    <h2 className="text-xl font-semibold mb-6">Administración de Clientes</h2>
                    <ClientAdministration />
                  </TabsContent>
                  
                  <TabsContent value="roles">
                    <h2 className="text-xl font-semibold mb-6">Gestión de Roles y Permisos</h2>
                    <RoleManagement />
                  </TabsContent>
                  
                  <TabsContent value="locations">
                    <h2 className="text-xl font-semibold mb-6">Gestión de Ubicaciones</h2>
                    <LocationsManagement />
                  </TabsContent>
                  
                  <TabsContent value="logs">
                    {/* Esta pestaña solo es un enlace a la página de logs */}
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Settings;